/**
 * 크레딧 충전 실패 페이지 — 토스 failUrl 도착점.
 *
 *  URL 파라미터: code, message, orderId
 *  토스가 실패 시 사장님을 여기로 보냄. BE 호출 없음 — 안내만.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { LayoutHeader } from "@/Header";
import { openMungAppDeepLink, wasOpenedFromMungApp } from "%/appDeepLink";

// 충전 흐름의 "크레딧으로" 이동 — 앱에서 넘겨온 외부 브라우저면 앱 복귀, 웹이면 SPA replace
const goToWallet = (navigate: ReturnType<typeof useNavigate>) => {
  if (wasOpenedFromMungApp()) {
    openMungAppDeepLink("/business/layout/user-credit-wallet", {
      query: {
        paymentReturn: "fail",
        ts: Date.now(),
      },
      type: "CREDIT_PAYMENT_RETURN",
    });
    return;
  }

  navigate({ to: "/business/layout/user-credit-wallet", replace: true });
};

export const Route = createFileRoute("/business/layout/user-credit-wallet-charge-fail")({
  component: ChargeFail,
});

function ChargeFail() {
  const navigate = useNavigate();
  const [info, setInfo] = useState<{ code: string; message: string; orderId: string }>({
    code: "",
    message: "",
    orderId: "",
  });

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    setInfo({
      code: p.get("code") ?? "UNKNOWN",
      message: p.get("message") ?? "결제가 완료되지 않았습니다.",
      orderId: p.get("orderId") ?? "",
    });
  }, []);

  // 자주 보이는 코드 한글 매핑
  const friendly = (code: string, raw: string) => {
    const map: Record<string, string> = {
      USER_CANCEL: "결제를 취소하셨어요.",
      INVALID_CARD_COMPANY: "카드사에서 거절되었어요. 다른 결제 수단을 시도해주세요.",
      EXCEED_MAX_DAILY_PAYMENT_COUNT: "오늘 결제 횟수를 초과했어요. 내일 다시 시도해주세요.",
      EXCEED_MAX_AMOUNT: "결제 한도를 초과했어요.",
      NOT_AVAILABLE_BANK: "선택한 은행은 현재 점검 중이에요.",
      INVALID_PASSWORD: "비밀번호가 일치하지 않아요.",
    };
    return map[code] || raw;
  };

  return (
    <>
      <LayoutHeader
        title="결제 실패"
        hasBackBtn
        hasFootPrint={false}
        onBackClick={() => navigate({ to: "/business/layout/user-credit-wallet", replace: true })}
      />
      <div className="flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5] px-4 py-8 space-y-4">
        <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-center">
          <div className="text-3xl">⚠</div>
          <h2 className="mt-2 text-lg font-bold text-red-800">결제가 완료되지 않았어요</h2>
          <p className="mt-2 text-sm text-red-700">{friendly(info.code, info.message)}</p>
        </div>

        <div className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
          <div className="flex justify-between">
            <span>오류 코드</span>
            <span className="font-mono">{info.code}</span>
          </div>
          {info.orderId && (
            <div className="mt-1 flex justify-between">
              <span>주문번호</span>
              <span className="font-mono">{info.orderId}</span>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => goToWallet(navigate)}
            className="flex-1 rounded-md border border-gray-300 py-3 text-sm font-medium text-gray-700"
          >
            크레딧으로
          </button>
          <button
            type="button"
            onClick={() => navigate({ to: "/business/layout/user-credit-wallet-charge", replace: true })}
            className="flex-1 rounded-md bg-[#FF9934] py-3 text-sm font-semibold text-white"
          >
            다시 시도
          </button>
        </div>
      </div>
    </>
  );
}
